import React, {FC, useState, useEffect} from "react"

import Flex from "@irati/ui/src/Flex"
import StepIndicator from "@irati/ui/src/StepIndicator"
import {Body, H4, Label} from "@irati/ui/src/Typography"
import Link, {RouterLink} from "@irati/ui/src/Link"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Close from "@irati/ui/src/Close"
import adminTracking from "@irati-tracking/admin/admin"

import {VIEW_BUSINESS_UNITS} from "../../../../../routes/routeConfig"
import {adminAccess, peopleDirectory, addCountry} from "../../SetupInformation/images"
import SetupStep from "../../SetupInformation/SetupStep"
import PeoplePageLink from "../../SetupInformation/PeoplePageLink"
import {InfoCard, StyledIcon} from "../../styled"
import SetupModal from "./SetupModal"

interface Props extends InjectIntl {
  onBack: () => void
}

const SetupInformation: FC<Props> = ({onBack, formatMessage}) => {
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [showSSOModal, setShowSSOModal] = useState<boolean>(false)
  const theme = useTheme()

  useEffect(() => {
    adminTracking.ssoSetup("set_up_summary_page")
  })

  const handleSetupITHelp = () => {
    setCurrentStep(2)
    adminTracking.ssoSetup("set_up_IT_help")
  }

  const handleSetupPeopleData = () => {
    setCurrentStep(3)
    adminTracking.ssoSetup("set_up_people_data")
  }

  const handleSetupCountryData = () => {
    setCurrentStep(4)
    adminTracking.ssoSetup("set_up_country_data")
  }

  return (
    <>
      <Flex margin="0 auto" paddingBottom={5} justifyContent="center">
        <StepIndicator currentStep={currentStep} stepsCount={4} />
        <Close onClick={onBack} right={theme.spacing.two} />
      </Flex>

      {currentStep === 1 && (
        <SetupStep
          title="admin.components.integrations.ssoSetup.step1.title"
          img={formatMessage({id: "admin.components.integrations.ssoSetup.step1.img"})}
          onButtonClick={handleSetupITHelp}
          checkboxText="admin.components.integrations.ssoSetup.step1.checkboxText"
          buttonText="admin.components.integrations.ssoSetup.step1.buttonText">
          <Body marginBottom={2}>
            <FormattedMessage
              id="admin.components.integrations.ssoSetup.step1.text.1"
              values={{
                link: (
                  <Link href={formatMessage({id: "admin.components.integrations.ssoSetup.step1.href"})} target="_blank">
                    <FormattedMessage id="admin.components.integrations.ssoSetup.step1.link" />
                  </Link>
                )
              }}
            />
          </Body>
          <Body>
            <FormattedMessage id="admin.components.integrations.ssoSetup.step1.text.2" />
          </Body>
          <Body marginBottom={2}>
            <FormattedMessage
              id="admin.components.integrations.ssoSetup.step1.text.3"
              values={{
                link: (
                  <Link href={formatMessage({id: "admin.components.integrations.ssoSetup.step1.workos.url"})} target="_blank">
                    <FormattedMessage id="admin.components.integrations.dsyncSetup.modal.workos.link" />
                  </Link>
                )
              }}
            />
          </Body>
        </SetupStep>
      )}
      {currentStep === 2 && (
        <SetupStep
          title="admin.components.integrations.ssoSetup.step2.title"
          applyBorder
          img={adminAccess}
          onButtonClick={handleSetupPeopleData}
          checkboxText="admin.components.integrations.dsyncSetup.step2.checkboxText">
          <Body marginBottom={3}>
            <FormattedMessage id="admin.components.integrations.dsyncSetup.step2.text" />
          </Body>
          <Flex marginBottom={4}>
            <PeoplePageLink />
          </Flex>
        </SetupStep>
      )}
      {currentStep === 3 && (
        <SetupStep
          title="admin.components.integrations.dsyncSetup.step3.title"
          img={peopleDirectory}
          applyBorder
          onButtonClick={handleSetupCountryData}
          checkboxText="admin.components.integrations.dsyncSetup.step3.checkboxText">
          <Body marginBottom={1}>
            <FormattedMessage id="admin.components.integrations.dsyncSetup.step3.fields" />
          </Body>
          <Body marginBottom={1}>
            <FormattedMessage id="admin.components.integrations.dsyncSetup.step3.field1" />
          </Body>
          <Body marginBottom={1}>
            <FormattedMessage id="admin.components.integrations.dsyncSetup.step3.field2" />
          </Body>
          <Body marginBottom={1}>
            <FormattedMessage id="admin.components.integrations.dsyncSetup.step3.field3" />
          </Body>
          <Body marginBottom={4}>
            <FormattedMessage id="admin.components.integrations.dsyncSetup.step3.field4" />
          </Body>
          <H4 marginBottom={1}>
            <FormattedMessage id="admin.components.integrations.ssoSetup.step3.header1" />
          </H4>
          <InfoCard padding={2} marginBottom={2} backgroundColor={theme.colors.tertiary.y_400}>
            <Flex justifyContent="space-between">
              <Flex marginTop={theme.spacing.half}>
                <StyledIcon icon={IconTypes.circleAlertFill} color={theme.colors.utility.ink} size={20} />
              </Flex>
              <Body maxWidth="100%" marginLeft={1}>
                <FormattedMessage
                  id="admin.components.integrations.ssoSetup.step3.alertBanner"
                  values={{
                    identifier: (
                      <Label large weight="bold">
                        <FormattedMessage id="admin.components.integrations.ssoSetup.step3.alertIdentifier" />
                      </Label>
                    )
                  }}
                />
              </Body>
            </Flex>
          </InfoCard>
          <Body marginBottom={4}>
            <FormattedMessage id="admin.components.integrations.ssoSetup.step3.text1" />
          </Body>
          <H4 marginBottom={1}>
            <FormattedMessage id="admin.components.integrations.ssoSetup.step3.header2" />
          </H4>
          <Body marginBottom={1}>
            <FormattedMessage id="admin.components.integrations.ssoSetup.step3.text2" />
          </Body>
          <Flex marginBottom={2}>
            <PeoplePageLink />
          </Flex>
        </SetupStep>
      )}
      {currentStep === 4 && (
        <SetupStep
          title="admin.components.integrations.ssoSetup.step4.title"
          img={addCountry}
          applyBorder
          onButtonClick={() => setShowSSOModal(true)}
          checkboxText="admin.components.integrations.dsyncSetup.step4.checkboxText">
          <Body marginBottom={3}>
            <FormattedMessage id="admin.components.integrations.dsyncSetup.step4.text" />
          </Body>
          <Flex marginBottom={4}>
            <RouterLink cta to={VIEW_BUSINESS_UNITS}>
              <FormattedMessage id="admin.components.integrations.dsyncSetup.step4.link" />
            </RouterLink>
            <Icon icon={IconTypes.arrowRight} color={theme.colors.primary1} marginLeft={theme.spacing.one} />
          </Flex>
        </SetupStep>
      )}
      <SetupModal show={showSSOModal} onHide={() => setShowSSOModal(false)} />
    </>
  )
}

export default injectIntl(SetupInformation)
